import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiClient } from '../../services/api';
import { isApiErrorStatus } from '../../services/api/http';
import { DueInstallment } from '../../types/paymentPlan';
import { formatCurrency } from '../../utils/currency';

type DueFilter = 'overdue' | 'today' | 'soon' | 'week' | 'month';
type Props = { onCustomerClick: (id: string, name: string) => void; initialFilter?: DueFilter };
const iso = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export default function InstallmentDueList({ onCustomerClick, initialFilter = 'overdue' }: Props) {
  const { language } = useLanguage();
  const tr = language === 'tr';
  const [filter, setFilter] = useState<DueFilter>(initialFilter);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<DueInstallment[]>([]);
  const [pages, setPages] = useState(0);
  const [available, setAvailable] = useState(Boolean(apiClient.paymentPlans));
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!apiClient.paymentPlans) return;
    let active = true;
    const now = new Date();
    const from = new Date(now); const to = new Date(now);
    if (filter === 'overdue') to.setDate(to.getDate() - 1);
    if (filter === 'soon') to.setDate(to.getDate() + 5);
    if (filter === 'week') to.setDate(to.getDate() + 6);
    if (filter === 'month') to.setMonth(to.getMonth() + 1, 0);
    const params = { from: filter === 'overdue' ? undefined : iso(from), to: iso(to), page, size: 10 };
    apiClient.paymentPlans.due(params).then(result => {
      if (active) { setRows(result.content); setPages(result.totalPages); setError(false); setAvailable(true); }
    }).catch(e => {
      if (active) { if (isApiErrorStatus(e, 404)) setAvailable(false); else setError(true); }
    });
    return () => { active = false; };
  }, [filter, page]);
  if (!available) return null;
  return <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2}>
      <Typography variant="h6">{tr ? 'Taksit takibi' : 'Installment tracking'}</Typography>
      <TextField select size="small" label={tr ? 'Vade' : 'Due date'} value={filter} onChange={e => { setFilter(e.target.value as DueFilter); setPage(0); }} sx={{ minWidth: 180 }}>
        <MenuItem value="overdue">{tr ? 'Gecikenler' : 'Overdue'}</MenuItem>
        <MenuItem value="today">{tr ? 'Bugün' : 'Today'}</MenuItem>
        <MenuItem value="soon">{tr ? '5 gün içinde' : 'Within 5 days'}</MenuItem>
        <MenuItem value="week">{tr ? '7 gün içinde' : 'Next 7 days'}</MenuItem>
        <MenuItem value="month">{tr ? 'Bu ay' : 'This month'}</MenuItem>
      </TextField>
    </Stack>
    {error && <Alert severity="error" sx={{ mt: 1 }}>{tr ? 'Taksitler yüklenemedi.' : 'Could not load installments.'}</Alert>}
    {rows.map(row => <Stack key={`${row.planId}-${row.sequenceNo}`} direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
      <Box><Button size="small" onClick={() => onCustomerClick(row.customerId, row.customerName)}>{row.customerName}</Button>
        <Typography variant="body2" color="text.secondary">{row.title || (tr ? 'Ödeme planı' : 'Payment plan')} · {row.sequenceNo}. {tr ? 'taksit' : 'installment'} · {row.dueDate}</Typography></Box>
      <Typography fontWeight={700}>{formatCurrency(row.remainingAmount)}</Typography>
    </Stack>)}
    {!error && rows.length === 0 && <Typography color="text.secondary" sx={{ mt: 2 }}>{tr ? 'Bu aralıkta açık taksit yok.' : 'No open installments in this period.'}</Typography>}
    {pages > 1 && <Stack direction="row" justifyContent="flex-end" alignItems="center"><Button disabled={page === 0} onClick={() => setPage(page - 1)}>{tr ? 'Önceki' : 'Previous'}</Button><Typography>{page + 1} / {pages}</Typography><Button disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>{tr ? 'Sonraki' : 'Next'}</Button></Stack>}
  </Paper>;
}
